import type { FC } from "react";
import { useCallback } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import Settings04Icon from "@untitled-ui/icons-react/build/esm/Settings04";
import ArrowOutwardOutlinedIcon from "@mui/icons-material/ArrowOutwardOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { useAuth } from "src/hooks/use-auth";

import { RouterLink } from "src/components/router-link";
import { paths } from "src/paths";
import { UserDataObj } from "src/types/snugtotal";
import { useRouter } from "src/hooks/use-router";
import useUserDataProvider from "src/contexts/snug/userData-context";

interface AccountPopoverProps {
  userData: UserDataObj | null;
  anchorEl: null | Element;
  onClose?: () => void;
  open?: boolean;
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const { userData, anchorEl, onClose, open, ...other } = props;
  const router = useRouter();
  const auth = useAuth();
  const { clientHouseholds, professionalGroup } = useUserDataProvider();

  const client_domain =
    professionalGroup?.white_label_config?.domain ||
    process.env.REACT_APP_CLIENT_APP_BASE_DOMAIN ||
    "";

  const leadLink = `https://${client_domain}/?at=${localStorage.getItem(
    "s_at"
  )}`;

  const ownHousehold = clientHouseholds?.find(
    (h) => h.head_of_household?.ud_id === userData?.ud_id
  );
  const ownHouseholdRoleId = ownHousehold?.household_role?.id;

  const handleOpenClientApp = useCallback((): void => {
    onClose?.();
    const url = ownHouseholdRoleId
      ? `${leadLink}&cr=${ownHouseholdRoleId}`
      : leadLink;
    window.open(url, "_blank");
  }, [onClose, leadLink, ownHouseholdRoleId]);

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      onClose?.();

      await auth.signOut();

      const href = paths.auth.jwt.entryStart;
      router.replace(href);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  }, [auth, onClose, router]);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "center",
        vertical: "bottom",
      }}
      disableScrollLock
      onClose={onClose}
      open={!!open}
      PaperProps={{ sx: { width: 200 } }}
      {...other}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">{userData?.full_name || ""}</Typography>
        <Typography color="text.secondary" variant="body2">
          {auth?.user?.username || ""}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 1 }}>
        <ListItemButton
          component={RouterLink}
          href={paths.dashboard.account}
          onClick={onClose}
          sx={{
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <ListItemIcon>
            <SvgIcon fontSize="small">
              <Settings04Icon />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body1">Settings</Typography>}
          />
        </ListItemButton>

        <ListItemButton
          onClick={handleOpenClientApp}
          sx={{
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <ListItemIcon>
            <SvgIcon fontSize="small">
              <ArrowOutwardOutlinedIcon />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body1">Open client app</Typography>}
          />
        </ListItemButton>
      </Box>
      <Divider sx={{ my: "0 !important" }} />
      <Box
        sx={{
          display: "flex",
          p: 1,
          justifyContent: "center",
        }}
      >
        <Button color="inherit" onClick={handleLogout} size="small">
          Logout
        </Button>
      </Box>
    </Popover>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
